'use client';

import React, { useState, useEffect, ReactNode } from 'react';
import { Header } from '@/components/ui/home/header';
import { BottomNavbar } from '@/components/ui/home/footer';
import { CartSidebar } from '@/components/ui/store/cart-sidebar';
import { CartErrorBoundary } from '@/components/error-boundary/store-error-boundary';
import { useLayout } from '@/contexts/LayoutContext';
import { useFooter, FooterMode } from '@/contexts/FooterContext';
import { useTenant } from '@/contexts/TenantContext';
import { getActiveTemplate } from '@/templates';

export interface BreadcrumbItem {
  label: string;
  href?: string;
  isActive?: boolean;
}

interface SubInfoProps {
  pageKey?: string;
  [key: string]: unknown;
}

interface FixedLayoutProps {
  children: ReactNode;
  showBackToArtworks?: boolean;
  breadcrumbs?: BreadcrumbItem[];
  subInfo?: SubInfoProps;
  footerMode?: FooterMode;
  onCartStateChange?: (isOpen: boolean) => void;
  inverted?: boolean;
}

export function FixedLayout({
  children,
  breadcrumbs = [],
  subInfo,
  footerMode: initialFooterMode = 'scroll',
  onCartStateChange,
  inverted = false,
}: FixedLayoutProps) {
  const { designId } = useTenant();
  const activeTemplate = getActiveTemplate(designId);
  const [isCatalogMenuOpen, setIsCatalogMenuOpen] = useState(false);
  const [isLeftMenuOpen, setIsLeftMenuOpen] = useState(false);
  const { isCartOpen, setIsCartOpen } = useLayout();
  const { setFooterMode, setIsFooterOpen } = useFooter();

  // Set footer mode from prop and reset footer open state on mount/mode change
  useEffect(() => {
    setFooterMode(initialFooterMode);
    setIsFooterOpen(false);
  }, [initialFooterMode, setFooterMode, setIsFooterOpen]);

  const handleCatalogMenuToggle = () => {
    setIsLeftMenuOpen(false);
    setIsCatalogMenuOpen(!isCatalogMenuOpen);
  };

  const handleLeftMenuToggle = () => {
    setIsCatalogMenuOpen(false);
    setIsLeftMenuOpen(!isLeftMenuOpen);
  };

  const handleCartToggle = () => {
    const newState = !isCartOpen;
    setIsCartOpen(newState);
    if (onCartStateChange) {
      onCartStateChange(newState);
    }
  };

  // Close menu when pressing Escape
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (isCartOpen) {
          setIsCartOpen(false);
        } else if (isCatalogMenuOpen || isLeftMenuOpen) {
          setIsCatalogMenuOpen(false);
          setIsLeftMenuOpen(false);
        }
      }
    };

    window.addEventListener('keydown', handleEscapeKey);
    return () => {
      window.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isCatalogMenuOpen, isLeftMenuOpen, isCartOpen, setIsCartOpen]);

  // Close menu when clicking outside
  useEffect(() => {
    if (!isCatalogMenuOpen && !isLeftMenuOpen) return undefined;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('[data-catalog-button]') && !target.closest('.catalog-mega-menu')) {
        setIsCatalogMenuOpen(false);
        setIsLeftMenuOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isCatalogMenuOpen, isLeftMenuOpen]);

  if (activeTemplate.Shell) {
    const ShellComponent = activeTemplate.Shell;
    return (
      <ShellComponent breadcrumbs={breadcrumbs} inverted={inverted}>
        {children}
      </ShellComponent>
    );
  }

  return (
    <div className={`min-h-screen flex flex-col ${inverted ? 'bg-white' : 'bg-black'}`}>
      {/* Fixed Top Navigation */}
      <div className={`fixed top-0 left-0 right-0 z-50 ${inverted ? 'bg-white' : 'bg-black'} shadow-md pointer-events-auto`}>
        {/* Ensure the header receives all pointer events */}
        <Header
          onCatalogMenuToggle={handleCatalogMenuToggle}
          isCatalogMenuOpen={isCatalogMenuOpen}
          onLeftMenuToggle={handleLeftMenuToggle}
          isLeftMenuOpen={isLeftMenuOpen}
          onCartToggle={handleCartToggle}
          breadcrumbs={breadcrumbs}
          inverted={inverted}
        />
      </div>

      {/* No separate catalog mega menu - it's now integrated into the header */}

      {/* Main Content Area - White scrollable container with padding for fixed elements */}
      {/* border-zinc-200 border*/}
      <div className="flex-1 relative">
        <div
          className={`fixedlayout ${inverted ? 'bg-black text-white' : 'bg-[var(--theme-surface-bg,#ffffff)] text-[var(--theme-surface-fg,#000000)]'} overflow-y-auto
          shadow-lg relative z-10
          transition-[margin-right] duration-[450ms] ease-[cubic-bezier(0.4,0,0.2,1)] motion-reduce:transition-none
          ${isCatalogMenuOpen || isLeftMenuOpen ? 'catalog-menu-open' : ''}
          ${initialFooterMode === 'hidden' ? 'no-footer' : ''}
          `}
          style={{
            marginLeft: 'var(--theme-frame-inset, 8px)',
            marginRight: isCartOpen ? '415px' : 'var(--theme-frame-inset, 8px)',
            borderRadius: 'var(--theme-radius-frame, 0.375rem)',
          }}
        >
          {children}
          {/* Keep portal content after children, including content resolved by Suspense. */}
          <div data-footer-slot />
        </div>
      </div>

      {/* Page actions that must stay reachable. The updates control lives above the footer. */}
      <BottomNavbar subInfo={subInfo} inverted={inverted} />

      {/* Cart Sidebar - In Black Background Area */}
      <CartErrorBoundary>
        <CartSidebar isOpen={isCartOpen} onClose={() => setIsCartOpen(false)} />
      </CartErrorBoundary>
    </div>
  );
}
